import {
  AccountBalance,
  ChevronLeft,
  CloudDownload,
  ContentCopy,
  Logout,
  Menu,
  Person,
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IConfig } from '../../Interfaces/IConfig';

const drawerWidth = 240;

const Main = styled('main')<{ open?: boolean }>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create(['width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: open ? `calc(100% - ${drawerWidth}px)` : '100%',
  boxSizing: 'border-box',
}));

const StyledAppBar = styled(AppBar)<{ open?: boolean }>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: open ? `calc(100% - ${drawerWidth}px)` : '100%',
  marginLeft: open ? drawerWidth : 0,
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

interface IItemsGroup {
  text: string;
  icon: JSX.Element;
  path: string;
}

interface IProps {
  children: ReactNode;
  configObject: IConfig;
}

export default function Layout({ children, configObject }: IProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isLaptop = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(false);

  useEffect(() => {
    if (isLaptop) {
      setMobileOpen(false);
    } else {
      setDesktopOpen(false);
    }
  }, [isLaptop]);

  const toggleDrawer = () => {
    if (isLaptop) {
      setDesktopOpen(!desktopOpen);
    } else {
      setMobileOpen(!mobileOpen);
    }
  };

  const closeDrawer = () => {
    setMobileOpen(false);
    setDesktopOpen(false);
  };

  const navigateTo = (path: string) => {
    navigate(path);
    closeDrawer();
  };

  const firstItemsGroup: IItemsGroup[] = [
    { text: 'Meine Vorgänge', icon: <AccountBalance />, path: '/' },
    {
      text: 'Meine Dokumente',
      icon: <CloudDownload />,
      path: '/download_area',
    },
    { text: 'Meine Einladungen', icon: <ContentCopy />, path: '/invitations' },
  ];

  const secondItemsGroup: IItemsGroup[] = [
    { text: 'Mein Benutzerkonto', icon: <Person />, path: '/account' },
  ];

  const renderDrawerContent = (showText: boolean, items: IItemsGroup[]) => (
    <List>
      {items.map(({ text, icon, path }) => (
        <ListItemButton key={path} onClick={() => navigateTo(path)}>
          <ListItemIcon>{icon}</ListItemIcon>
          {showText && <ListItemText primary={text} />}
        </ListItemButton>
      ))}
    </List>
  );

  return (
    <Box sx={{ display: 'flex', width: '100vw', overflowX: 'hidden' }}>
      <CssBaseline />
      <StyledAppBar position='fixed' open={isLaptop && desktopOpen}>
        <Toolbar>
          {(!isLaptop || !desktopOpen) && (
            <IconButton
              color='inherit'
              edge='start'
              onClick={toggleDrawer}
              sx={{ mr: 2 }}
            >
              <Menu />
            </IconButton>
          )}
          <Typography variant='h6' noWrap>
            {isLaptop ? `DiNo - ${configObject.company_name}` : 'DiNo'}
          </Typography>
          <Box flexGrow={1} />
          <Typography sx={{ mr: 2 }}>{configObject.version}</Typography>
          <IconButton
            title='Logout'
            onClick={() => {
              localStorage.removeItem('SessionToken');
              window.location.reload();
            }}
          >
            <Logout />
          </IconButton>
        </Toolbar>
      </StyledAppBar>

      {!isLaptop && (
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={closeDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          <DrawerHeader>
            <IconButton onClick={closeDrawer}>
              <ChevronLeft />
            </IconButton>
          </DrawerHeader>
          {renderDrawerContent(true, firstItemsGroup)}
          <Divider />
          {renderDrawerContent(true, secondItemsGroup)}
        </Drawer>
      )}

      {isLaptop && (
        <Drawer
          variant='permanent'
          sx={{
            width: desktopOpen ? drawerWidth : theme.spacing(7),
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: desktopOpen ? drawerWidth : theme.spacing(7),
              boxSizing: 'border-box',
              overflowX: 'hidden',
            },
          }}
          onMouseEnter={() => setDesktopOpen(true)}
          onMouseLeave={() => setDesktopOpen(false)}
        >
          <DrawerHeader>
            <IconButton onClick={closeDrawer}>
              <ChevronLeft />
            </IconButton>
          </DrawerHeader>
          {renderDrawerContent(desktopOpen, firstItemsGroup)}
          <Divider />
          {renderDrawerContent(desktopOpen, secondItemsGroup)}
        </Drawer>
      )}

      <Main open={isLaptop && desktopOpen}>
        <DrawerHeader />
        <Box sx={{ maxWidth: '100%', overflowX: 'hidden' }}>{children}</Box>
      </Main>
    </Box>
  );
}
